type SetAreas = {
  element: HTMLElement;
  isApplying: boolean;
};

/**
 * // Class Obj: 要素のフェードインエフェクト
 *
 * 対象要素を IntersectionObserver で監視し、
 * ビューポート内に入ったタイミングで CSS クラスを付与する。
 */
export class FadeInEffect
{
  // 監視対象の情報
  private mSetAreas: SetAreas[];

  // IntersectionObserver インスタンス
  private mObserver: IntersectionObserver | null = null;

  /**
   * // Constructor: 対象となる要素コレクションを受け取り、それらを HTMLElement 配列に整形する
   *
   * @param elements 対象となる要素の HTMLCollection
   */
  constructor(elements: HTMLCollectionOf<Element>)
  {
    this.mSetAreas = Array.from(elements).map((element: Element) =>
    ({
      element: element as HTMLElement,
      isApplying: false,
    }));
  }

  /**
   * // Method: IntersectionObserver を使ってスタイルを適用
   *
   * @param apply_class 適用する CSS クラス名
   * @param threshold   どの程度見えたら発火するか（0〜1、デフォルト 0.2）
   */
  public observe(apply_class: string, threshold: number = 0.2): void
  {
    // 対象要素が存在しない場合は何もしない
    if (this.mSetAreas.length === 0)
    {
      return;
    }

    // 既にオブザーバーを作成済みなら再利用
    if (this.mObserver)
    {
      // すでに observe 済みだが、別クラスで再利用したいケースなどを考えるなら
      // ここで一度 disconnect() して再度セットアップしてもよい
      this.mObserver.disconnect();
    }

    // IntersectionObserver 非対応ブラウザ向けフォールバック
    if (!('IntersectionObserver' in window))
    {
      // 古いブラウザでは、とりあえず全ての要素に即時クラス付与
      this.applyImmediately(apply_class);
      return;
    }

    // IntersectionObserver のコールバック関数
    const callback: IntersectionObserverCallback = (entries, observer) =>
    {
      entries.forEach((entry: IntersectionObserverEntry) =>
      {
        if (entry.isIntersecting)
        {
          const target = entry.target as HTMLElement;

          // 対象の SetAreas を取得
          const area = this.mSetAreas.find(($area) => $area.element === target);
          if (!area || area.isApplying)
          {
            return;
          }

          // CSS クラスを付与
          target.classList.add(apply_class);
          area.isApplying = true;

          // 一度適用したら監視解除（パフォーマンス向上）
          observer.unobserve(target);
        }
      });
    };

    // IntersectionObserver のオプション
    const options: IntersectionObserverInit = {
      root: null,           // ビューポートをルートとする
      rootMargin: '0px',    // マージンなし
      threshold: threshold, // 指定した割合だけ見えたときに発火
    };

    // オブザーバーを生成
    this.mObserver = new IntersectionObserver(callback, options);

    // 各要素を監視対象に登録
    this.mSetAreas.forEach(($area) =>
    {
      this.mObserver!.observe($area.element);
    });
  }

  /**
     * // Method: フォールバック用・即時適用
     *
     * IntersectionObserver 非対応ブラウザ向けに、
     * すべての対象要素に即座にクラスを付与する。
     */
  /**
   * // Method: IntersectionObserver を使わず即時フェードインエフェクトを適用
   *
   * IntersectionObserver を使わず、即時フェードインエフェクトを適用するメソッド
   * IntersectionObserver がサポートされていないブラウザ向け(フォールバック用)
   * フォールバック関数を提供する
   *
   * @param apply_class 適用する CSS クラス名
   */
  private applyImmediately(apply_class: string): void
  {
    this.mSetAreas.forEach(($area) =>
    {
      if (!$area.isApplying)
      {
        $area.element.classList.add(apply_class);
        $area.isApplying = true;
      }
    });
  }
}
